/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";

import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import Typography from "@mui/material/Typography";

import { colors } from "@dotnet/telematics-design-system";

import Separator from "../../../../../../components/Separator/Separator";
import { useFormattedNumber } from "../../../../../../shared";
import { TooltipDataType } from "../../../types";
import { useStyles } from "./styles";

interface CreditsTooltipProps {
  tooltip: TooltipDataType | null;
  isHovered: boolean;
}

export const CreditsTooltip = ({ tooltip, isHovered }: CreditsTooltipProps) => {
  const { renderFormattedNumber } = useFormattedNumber();

  const { t: translate } = useTranslation();

  const safeTooltip = tooltip || {
    visible: false,
    x: -9999,
    y: -9999,
    label: "",
    date: "",
    isLastElementInchart: false
  };

  const { customTooltip, labelDot } = useStyles(safeTooltip, isHovered);

  const isReadyToShow =
    tooltip?.visible &&
    typeof tooltip.x === "number" &&
    typeof tooltip.y === "number" &&
    tooltip.x > 0 &&
    tooltip.y > 0;

  const { label, date, deducted, added } = safeTooltip;

  const showDeducted = deducted !== undefined && deducted !== 0;
  const showAdded = added !== undefined && added !== 0;
  const showSeparator = showDeducted || showAdded;

  const tooltipLabelSpan = translate("Dashboard##credits");

  return ReactDOM.createPortal(
    <div
      css={css([
        customTooltip,
        {
          opacity: isReadyToShow ? 1 : 0,
          pointerEvents: "none",
          transition: "opacity 0.2s ease-in-out"
        }
      ])}
      data-testid="credits-tooltip"
    >
      <Typography
        variant="body2Medium"
        sx={{ display: "flex", alignItems: "center" }}
      >
        <span css={css(labelDot)}> </span>
        {tooltipLabelSpan}: {renderFormattedNumber(label as number)}
      </Typography>

      <Typography variant="body2Regular" component="div">
        {date}
      </Typography>

      {showSeparator && <Separator customStyle={{ margin: "4px 0" }} />}

      {showDeducted && (
        <div>
          <Typography variant="body2Regular">
            {translate("Dashboard##deducted")}:{" "}
          </Typography>

          <Typography
            variant="body2Medium"
            sx={{
              color: colors.red800
            }}
          >
            {renderFormattedNumber(deducted)}
          </Typography>
        </div>
      )}

      {showAdded && (
        <div>
          <Typography variant="body2Regular">
            {translate("Dashboard##added")}:{" "}
          </Typography>

          <Typography variant="body2Medium" sx={{ color: colors.green900 }}>
            + {renderFormattedNumber(added)}
          </Typography>
        </div>
      )}
    </div>,
    document.body
  );
};
